import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TDR } from 'tdr-common';
import { analytics, LINK_CLICKED_EVENT } from '../../helpers/analytics';
import { resolveLocalizedText } from '../../helpers/resolveLocalizedText';
import { resolveClosedDateCtaHref, type ClosedDateLinkConfig } from '../../helpers/closedDateConfig';
import { NoticeTile } from '../notice-cards/NoticeTile';

export interface ClosedDateLinkNoticeProps {
	restaurant: TDR.Restaurant;
	config: ClosedDateLinkConfig;
}

export const ClosedDateLinkNotice = ({ restaurant, config }: ClosedDateLinkNoticeProps) => {
	const { t, i18n } = useTranslation();
	const location = useLocation();
	const href = resolveClosedDateCtaHref(config.cta, location.search);
	const title = resolveLocalizedText(
		config.title ?? t('notice_cards.closed_date_title', { defaultValue: 'Unavailable on this date' }),
		i18n.language
	);
	const body = resolveLocalizedText(
		config.message ?? '',
		i18n.language
	);

	useEffect(() => {
		if (!href) {
			return;
		}
		analytics.track('Notice Viewed', {
			notice: 'ClosedDateLinkNotice',
			restaurantId: restaurant?.id,
			ctaHref: href
		});
	}, [restaurant?.id, href]);

	if (!href || !body) {
		return null;
	}

	const handleClick = () => {
		analytics.track(LINK_CLICKED_EVENT, {
			href,
			purpose: 'Redirect from Closed Date Link Notice',
			restaurantId: restaurant?.id
		});
	};

	return (
		<NoticeTile
			icon="event"
			title={title}
			subtitle={body}
			href={href}
			external={!config.cta.slug?.trim()}
			onClick={handleClick}
			data-cy="closed-date-link-notice"
		/>
	);
};
